import React, {ReactElement} from 'react';
import {Box, Container} from '@mui/material';
import {Form, Formik, FormikErrors} from 'formik';
import {isEmpty} from 'lodash';
import FormikTextField from 'src/components/FormikTextField';
import Label from 'shared/components/label';
import Button from 'src/components/button';
import req from 'shared/req';
import {useHistory} from 'react-router-dom';
import {errorMessage, flashMessage} from 'shared/actions/notifications';
import {useDispatch} from 'react-redux';
import Notifications from 'shared/components/notifications';
import {useBoolean} from 'src/hooks/useBoolean';
import useBrandName from 'src/hooks/use-brand-name';

interface FormValues {
  password: string;
  confirmPassword: string;
}

const validate = (values: FormValues): FormikErrors<FormValues> => {
  const errors: FormikErrors<FormValues> = {};

  if (isEmpty(values.password)) {
    errors.password = 'Password is required';
  } else if (values.password.length < 8) {
    errors.password = 'Password must include at least 8 characters.';
  } else if (values.password.length > 128) {
    errors.password = 'Password must be no more than 128 characters.';
  } else if (!/[A-Z]/.test(values.password)) {
    errors.password = 'Password must include at least 1 uppercase letter.';
  } else if (!/[a-z]/.test(values.password)) {
    errors.password = 'Password must include at least 1 lowercase letter.';
  } else if (!/[0-9]/.test(values.password)) {
    errors.password = 'Password must include at least 1 number.';
  }

  if (values.password !== values.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }
  return errors;
};

export default function CollegePortalResetPassword(): ReactElement {
  const dispatch = useDispatch();
  const history = useHistory();
  const loading = useBoolean(false);
  const brandName = useBrandName();

  const handleSubmit = async (values: FormValues): Promise<void> => {
    loading.setTrue();

    try {
      await req({
        url: '/college-portal/reset-password',
        method: 'POST',
        data: {
          password: values.password,
          password_confirmation: values.confirmPassword,
        },
      });

      dispatch(flashMessage('Successfully reset password!'));
      history.go(0);
    } catch {
      dispatch(errorMessage());
    } finally {
      loading.setFalse();
    }
  };
  return (
    <Box
      id="page-content-wrapper"
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <Notifications />
      <Box>
        <Container>
          <h3 className="shared-font dislay-1">
            Welcome to {brandName}. For your security, please set a new password.
          </h3>
          <Label>
            Change Password
            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
              }}
              onSubmit={handleSubmit}
              validate={validate}
            >
              {(formikProps): ReactElement => (
                <Form className="raise-form">
                  <div className="form-group">
                    <FormikTextField
                      type="password"
                      name="password"
                      placeholder="New Password"
                      aria-label="password"
                      onChange={formikProps.handleChange('password')}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <FormikTextField
                      type="password"
                      name="confirmPassword"
                      placeholder="Confirm New Password"
                      aria-label="confirm password"
                      onChange={formikProps.handleChange('confirmPassword')}
                      className="form-group form-control"
                    />
                  </div>
                  <Button type="submit" disabled={loading.value || !isEmpty(formikProps.errors)}>
                    {loading.value ? 'Submitting' : 'Update Password'}
                  </Button>
                </Form>
              )}
            </Formik>
          </Label>
        </Container>
      </Box>
    </Box>
  );
}
